import React, { FC, useMemo } from 'react';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { PhoneNumberUtil, PhoneNumberFormat as PNF } from 'google-libphonenumber';
import styled from 'styled-components';

import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';
import AudioSection from 'src/presentation/components/pages/channels/voice/components/AudioSection';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { CheckboxWithBoldLabel } from 'src/presentation/components/forms/Checkbox';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import { FieldWrapper } from '@ad-scripts/presentation/components/common/input_field';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import {
  HOLD_MUSIC_PATH,
  IVR_ID_PATH,
  IVR_WAIT_DURATION_PATH,
  QUEUE_IVR_PATH,
  QUEUE_URL_PATH,
} from 'src/presentation/components/pages/channels/voice/constants/voiceConstants';
import Input from 'src/presentation/components/forms/Input';
import { NanoSecondsInputField } from 'src/presentation/components/pages/shared/NanoSecondsInputField';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import { getPublishedIvrs } from '../../shared/helpers/ivrsHelper';
import voiceConfigurationResource from 'src/actions/channels/voiceConfigurationResource';
import useOnSave from './hooks/useOnSave';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const phoneUtil = PhoneNumberUtil.getInstance();
const HoldMusicDescription = 'The music customers hear when the call is put on hold.';
const QueueMusicDescription = 'The music customers hear while waiting to get connected to an agent.';

const VoiceEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { values: ivrConfigurations } = useResourceValues(ivrsResource);

  const isLoopHoldMusicEnabled = useIsFeatureEnabled()('loopHoldMusic');

  const ivrs = useMemo(() => getPublishedIvrs(ivrConfigurations), [ivrConfigurations]);

  const { update, validate } = voiceConfigurationResource.effects;
  const { resetErrors } = voiceConfigurationResource.actions;

  const formattedPhoneNumber = phoneUtil.format(phoneUtil.parse(item.number), PNF.INTERNATIONAL);

  const ivrsDropdownOptions = useMemo(
    () => [
      { label: 'None', value: '' },
      ...orderBy(
        ivrs.map(({ id, name, description }: any) => ({ label: name || description || id, value: id })),
        ['label'],
        ['asc']
      ),
    ],
    [ivrs]
  );

  const onSaveHandler = useOnSave();

  const formContext = useForm({
    errors,
    item,
    onSave: onSaveHandler,
    resetErrors,
    root,
    update,
    validate,
  });
  const { workingState, onClose, onSave } = formContext;

  const description = (
    <FormDescription>
      Modify your voice configuration for <b>{formattedPhoneNumber}</b>. Review how to{' '}
      <ExternalLink
        data-aid="set-up-voice-help-link"
        label="manage phone numbers"
        to="https://help.gladly.com/implementation/docs/set-up-voice-ivr"
      />
      {'.'}
    </FormDescription>
  );

  const queueIvr = get(workingState, QUEUE_IVR_PATH);

  return (
    <Form context={formContext}>
      <PrimaryHeader>Edit voice configuration</PrimaryHeader>

      <Body data-aid="channels-voice-editor">
        <Section id="voice-configuration" description={description}></Section>
        <Section>
          <StyledRow>
            <Input
              data-aid="channels-voice-internal-name-input"
              attr="name"
              autoFocus
              label="Internal name"
              placeholder="Internal name"
              description="A nickname that makes it easier to identify this Entry Point at a glance."
            />
          </StyledRow>
          <StyledRow>
            <Dropdown
              allowEmptyValues
              attr={IVR_ID_PATH}
              data-aid="channels-voice-initial-ivr-dropdown"
              description="Customers immediately get routed to the first node (step) of this IVR when they call this number."
              label="Initial IVR"
              options={ivrsDropdownOptions}
              searchable
            />
          </StyledRow>
          <StyledRow>
            <Dropdown
              allowEmptyValues
              attr={QUEUE_IVR_PATH}
              data-aid="channels-voice-queue-ivr-dropdown"
              description="The IVR a customer gets routed to after a certain amount of time (seconds) has passed."
              label="Queue IVR"
              options={ivrsDropdownOptions}
              searchable
            />
          </StyledRow>
          {queueIvr && (
            <CallTimeoutRow>
              <StyledNanoSecondsInputField
                attr={IVR_WAIT_DURATION_PATH}
                className="nanoseconds-input"
                data-aid="channels-voice-outgoing-call-timeout-input"
                description="The amount of time customers wait for an agent to become available. Timer resets once call banner is presented to an agent."
                label="Queue IVR wait time (seconds)"
              />
            </CallTimeoutRow>
          )}
          <AudioSection
            attr={QUEUE_URL_PATH}
            description={QueueMusicDescription}
            formContext={formContext}
            itemId={item.id}
            label="Queue music"
          />
          <AudioSection
            attr={HOLD_MUSIC_PATH}
            description={HoldMusicDescription}
            formContext={formContext}
            itemId={item.id}
            label="Hold music"
          />
          {isLoopHoldMusicEnabled && (
            <>
              <Row>
                <CheckboxWithBoldLabel
                  attr="loopHoldMusic"
                  data-aid="loopHoldMusic"
                  label="Loop the queue and hold music"
                  description="When selected, the music will repeat from the beginning. Callers will hear silence if the recording is not long enough and looping is not activated."
                />
              </Row>
              <Row>
                <StyledAlertInfo>
                  <b>Note:&nbsp;</b>
                  We recommend keeping the length of your queue or hold music under 5 minutes and activating the
                  &quot;Loop the queue and hold music&quot; setting.
                </StyledAlertInfo>
              </Row>
            </>
          )}
        </Section>
      </Body>

      <Footer>
        <Button data-aid="admin-channels-voice-editor-cancel" type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="admin-channels-voice-editor-save" onClick={onSave}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

const FormDescription = styled.span``;

const PrimaryHeader = styled(H1)`
  margin-bottom: ${p => p.theme.spacing.stackXLarge};
`;

const StyledRow = styled(Row)`
  margin-bottom: ${p => p.theme.spacing.stackXLarge};
`;

const CallTimeoutRow = styled(StyledRow)`
  input {
    max-width: 62px;
  }
`;

const StyledNanoSecondsInputField = styled(NanoSecondsInputField)`
  ${FieldWrapper} {
    max-width: 80px;

    &:after {
      content: 's';
      padding: 1px 0;
      position: relative;
      right: 2px;
    }

    input {
      width: 100%;
    }
  }
`;

const StyledAlertInfo = styled(AlertInfo)`
  line-height: 20px;
  > svg {
    margin-top: 5px;
  }
`;

export default VoiceEditor;
